import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/buttons';
import { LogoWordmark, Text } from '@/components/ui/display';
import { Alert } from '@/components/ui/feedback';
import { Card } from '@/components/ui/surfaces';
import { OrganizationPickerDialog } from '@/features/auth/OrganizationPickerDialog';
import { useSessionEnd } from '@/features/auth/useSession';
import { signInFailureKey, useGoogleSignIn, useSelectOrganization } from '@/features/auth/useSignIn';
import type { Organization } from '@/features/organization/types';
import { isForbidden } from '@/lib/apiClient';

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.4 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.1 0 11.2-2 15-5.5l-7.5-5.8c-2.1 1.4-4.7 2.2-7.5 2.2-6.3 0-11.7-3.9-13.6-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z"
      />
    </svg>
  );
}

/** Shows the Google sign-in, and the organization picker when the session has several. */
export function LoginPage() {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<Organization[]>([]);
  const [token, setToken] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const sessionEnd = useSessionEnd();
  const select = useSelectOrganization();
  const signIn = useGoogleSignIn({
    onUndecided: (organizations, nextToken) => {
      setChoices(organizations);
      setToken(nextToken);
    },
  });

  function onSelect(organizationId: string) {
    setPendingId(organizationId);
    select.mutate({ organizationId, token }, { onSettled: () => setPendingId(null) });
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-6">
      <OrganizationPickerDialog
        open={choices.length > 1}
        organizations={choices}
        pendingId={pendingId}
        failure={select.isError ? (isForbidden(select.error) ? 'refused' : 'failed') : null}
        onSelect={onSelect}
      />

      <Card className="flex w-full max-w-sm flex-col items-center gap-5 p-8 text-center sm:px-8 sm:py-10">
        <LogoWordmark />

        <div className="flex flex-col gap-1.5">
          <Text as="p" variant="pageTitle" tone="heading">
            {t('app.name')}
          </Text>
          <Text as="p" tone="muted">
            {t('app.tagline')}
          </Text>
        </div>

        {signIn.isError ? (
          <Alert variant="error" className="w-full text-left">
            {t(signInFailureKey(signIn.error))}
          </Alert>
        ) : (
          sessionEnd &&
          sessionEnd !== 'requested' && (
            <Alert variant="warning" className="w-full text-left">
              {t(`login.ended.${sessionEnd}`)}
            </Alert>
          )
        )}

        <Button
          variant="secondary"
          size="lg"
          fullWidth
          loading={signIn.isPending}
          iconLeft={<GoogleMark />}
          onClick={() => signIn.mutate()}
        >
          {signIn.isPending ? t('login.signingIn') : t('login.signInWithGoogle')}
        </Button>
      </Card>

      <nav aria-label={t('login.legalLinks')} className="flex gap-1">
        {(['imprint', 'privacy', 'terms'] as const).map((key) => (
          <Button key={key} variant="ghost" size="sm" disabled>
            {t(`common.${key}`)}
          </Button>
        ))}
      </nav>
    </div>
  );
}
